import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { trashEntity } from '../../services/api'
import ConfirmTrashModal from '../shared/ui/ConfirmTrashModal'

/**
 * Mise à la corbeille du CONTENU ENTIER ouvert dans le builder — le pendant,
 * depuis l'éditeur, du picto corbeille d'une ligne de la liste des contenus d'un
 * site (`pages/site.yaml`).
 *
 * POURQUOI DU REACT ICI (rituel config-first) : la barre du builder n'est pas un
 * rjBuild — c'est l'éditeur React, dont l'état de page (`useBuilder`) est la
 * source de vérité et qui connaît seul le nid de la page ouverte. Aucun YAML
 * reactive-json ne s'y monte.
 *
 * Ce fichier n'ajoute AUCUNE mécanique : la confirmation est la pop-in COMMUNE à
 * tous les gestes destructifs d'Orbit (`ConfirmTrashModal`, déjà utilisée par le
 * wiki, les projets, les tâches et les corbeilles), et l'appel est le point d'API
 * générique `POST /api/orbit/trash` (`trashEntity`). Il ne porte que ce qui est
 * propre au builder : la cible, l'avertissement « travail non enregistré », et
 * l'affichage du refus.
 *
 * Suppression = SOFT delete : le module `trash` intercepte `$entity->delete()`
 * et horodate le champ `deleted` — le contenu part à la corbeille, d'où le
 * gestionnaire du site (`/sites/:gid/corbeille`) peut le sortir tant que la
 * purge automatique n'est pas passée.
 *
 * Le geste n'est conditionné à AUCUN droit calculé côté front : c'est Drupal qui
 * tranche (`canTrash` → `$entity->access('delete')`), et l'échec s'affiche ici —
 * comme pour « Modifier » dans la liste des contenus.
 */
export const BuilderPageTrashModal = ({ page, dirty = false, onClose, onTrashed }) => {
  const [trashing, setTrashing] = useState(false)
  const [error, setError] = useState('')

  const handleTrash = async () => {
    setTrashing(true)
    setError('')
    try {
      await trashEntity('node', 'builder_page', page.pageNid)
      onTrashed()
    } catch {
      // Le message de Drupal est technique et anglais : on dit la cause la plus
      // probable, la seule qui soit actionnable par un rédacteur. Même phrase
      // que la liste des contenus d'un site.
      setError("La mise à la corbeille a échoué : vous n'avez peut-être pas le droit de supprimer ce contenu.")
      setTrashing(false)
    }
  }

  return (
    <ConfirmTrashModal
      isOpen
      // Portail + z-index au-dessus du chrome : le canevas du builder empile
      // haut (un bloc peut déclarer `z-[100]`, le panneau Assistant est en
      // `z-[200]`) et transpercerait la pop-in rendue en place au z-50.
      elevated
      onConfirm={handleTrash}
      onCancel={trashing ? () => {} : onClose}
      title={`Mettre « ${page.title || page.slug} » à la corbeille ?`}
      description="Ce contenu ne sera plus servi au public ni listé dans les contenus du site. Rien n'est perdu : il part à la corbeille, d'où un administrateur peut le restaurer tant que la purge automatique (90 jours) n'est pas passée."
      confirmLabel="Mettre à la corbeille"
      loading={trashing}
      error={error}
    >
      {dirty && (
        <p className="alert alert-warning mt-4 text-left">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>Les modifications en cours n'ont pas été enregistrées : elles seront perdues.</span>
        </p>
      )}
    </ConfirmTrashModal>
  )
}
